import PropTypes from 'prop-types';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';

// material-ui
import { makeStyles, useTheme } from '@mui/styles';
import { AppBar, CssBaseline, Toolbar, useMediaQuery } from '@mui/material';

// project imports
import Breadcrumbs from '../ui-component/extended/Breadcrumbs';
import Header from './Header';
import navigation from '../menu-items';
import { drawerWidth } from '../store/constant';
import { SET_MENU } from '../store/actions';

// assets
import { IconChevronRight } from '@tabler/icons-react';

import FixedMenuLayout from './FixedMenuLayout';
// style constant

const drawerWidthOpen = 220;
const drawerWidthClose = 0;

/**
 * useStyles
 *
 * @summary Generates JSS styles for the MainLayout component.
 *
 * @description
 * Uses Material-UI's makeStyles to create a style object for the MainLayout. Defines styles for content area, content shift, root container, app bar, app bar width, and footer. Applies responsive layout adjustments and transitions for sidebar open/close states and different breakpoints.
 *
 * @returns {Object} The generated classes object containing style definitions for the MainLayout component.
 *
 * @example
 * const classes = useStyles();
 */
const useStyles = makeStyles((theme) => ({
    content: {
        flexGrow: 1,
        transition: theme.transitions.create(["margin"], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
        }),
        marginLeft: drawerWidthClose,
        width: `calc(100% - ${drawerWidthClose}px)`,
    },
    contentShift: {
        marginLeft: drawerWidthOpen,
        width: `calc(100% - ${drawerWidthOpen}px)`,
        transition: theme.transitions.create(["margin"], {
            easing: theme.transitions.easing.easeOut,
            duration: theme.transitions.duration.enteringScreen,
        }),
    },
    root: {
        display: 'flex'
    },
    appBar: {
        backgroundColor: '#ECECEC',
        color: theme.palette.background.secondaryLight
    },
    appBarWidth: {
        transition: theme.transitions.create('width'),
        backgroundColor: '#ECECEC'
    },
    content: {
        ...theme.typography.mainContent,
        borderBottomLeftRadius: 0,
        borderBottomRightRadius: 0,
        transition: theme.transitions.create('margin', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen
        }),
        [theme.breakpoints.up('md')]: {
            marginLeft: 5, //-(drawerWidth - 20),
            width: `calc(100% - ${drawerWidth}px)`
        },
        [theme.breakpoints.down('md')]: {
            marginLeft: '5px',
            width: `calc(100% - ${drawerWidth}px)`,
            padding: '5px'
        },
        [theme.breakpoints.down('sm')]: {
            marginLeft: '5px',
            width: `calc(100% - ${drawerWidth}px)`,
            padding: '5px',
            marginRight: '5px'
        }
    },
    contentShift: {
        transition: theme.transitions.create('margin', {
            easing: theme.transitions.easing.easeOut,
            duration: theme.transitions.duration.enteringScreen
        }),
        // marginLeft: drawerWidth ,
        borderBottomLeftRadius: 0,
        borderBottomRightRadius: 0,
        [theme.breakpoints.down('md')]: {
            marginLeft: '5px'
        },
        [theme.breakpoints.down('sm')]: {
            marginLeft: '5px'
        }
    },
    footer: {
        marginBottom: '10px'
    },
}));

//-----------------------|| MAIN LAYOUT ||-----------------------//

const MainLayout = ({ children }) => {
    const classes = useStyles();
    const theme = useTheme();
    const matchDownMd = useMediaQuery(theme.breakpoints.down('md'));
    const location = useLocation();
    const path = location.pathname;
    // Handle left drawer
    const leftDrawerOpened = useSelector((state) => state.customization.opened);
    const user = useSelector((state) => state.account.user);
    const dispatch = useDispatch();
    const handleLeftDrawerToggle = () => {
        dispatch({ type: SET_MENU, opened: !leftDrawerOpened });
    };
    /**
 * useEffect (responsive menu state)
 *
 * @summary Updates the left drawer menu state based on the screen size breakpoint.
 *
 * @description
 * Watches the matchDownMd media query value. When the screen size crosses the "md" breakpoint, dispatches a SET_MENU action to open or close the left drawer accordingly. Uses a timeout for smoother transitions on smaller screens.
 *
 * @param {boolean} matchDownMd - True if the screen width is below the "md" breakpoint.
 *
 * @returns {void}
 *
 * @example
 * useEffect(() => {
 *   // responsive menu state
 * }, [matchDownMd]);
 */
    React.useEffect(() => {
        if (!matchDownMd) {
            dispatch({ type: SET_MENU, opened: !matchDownMd });
        } else {
            setTimeout(() => {
                dispatch({ type: SET_MENU, opened: !matchDownMd });
            }, 300)
        }
    }, [matchDownMd]);

    const noMenuPages = [
        '/dashboard',
        '/user_profile_list',
        '/organization_profile_list'
    ];

    const isNoMenuPage = noMenuPages.includes(path);

    return (
        <div className={classes.root} style={{ backgroundColor: theme.palette.themeMode == 'dark' ? theme.palette.primary.light : theme.palette.primary.dark }}>
            <CssBaseline />
            <AppBar
                position="fixed"
                elevation={0}
                className={leftDrawerOpened ? `${classes.appBarWidth} no-print` : `${classes.appBar} no-print`}
            >
                <Toolbar style={{ backgroundColor: theme.palette.themeMode == 'dark' ? "#1E1E30" : theme.palette.primary.dark }}>
                    {(path == '/dashboard' || path == '/logout' || path == '/user_profile_list' || path == '/organization_profile_list') &&
                        <Header handleLeftDrawerToggle={handleLeftDrawerToggle} />
                    }
                </Toolbar>
                {(path != '/dashboard' && path != '/logout' && path != '/user_profile_list' && path != '/organization_profile_list') && <FixedMenuLayout />}
            </AppBar>

            <main
                style={{
                    backgroundColor: theme.palette.themeMode == 'dark'
                        ? theme.palette.primary.light
                        : theme.palette.primary.dark,
                    transition: "margin-left 0.3s ease",
                    marginLeft: isNoMenuPage ? 0 : (leftDrawerOpened ? 220 : 0),
                    marginTop: 56,
                    flexGrow: 1,
                    minHeight: "calc(100vh - 64px)",
                    padding: "16px",
                    boxSizing: "border-box",
                }}
            >
                <Breadcrumbs separator={IconChevronRight} navigation={navigation} icon title rightAlign />
                <div>{children}</div>
            </main>
        </div>
    );
};

MainLayout.propTypes = {
    children: PropTypes.node
};

export default MainLayout;
